import React from 'react';
import { Button, Card, Space, Typography } from 'antd';
import {
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  InfoCircleTwoTone,
  QuestionCircleTwoTone,
} from '@ant-design/icons';
import { Link, useSearchParams } from 'react-router-dom';
import useRouterWithLang from '../../core/hooks/useRouterWithLang';
import { AppRouteKey } from '../../core/utilities/router';
import usePlatform from '../../core/hooks/usePlatform';
import { useAppSelector } from '../../core/services/hooks';
import useContent from '../../core/hooks/useContent';

const { Title, Text } = Typography;

type ResultContent = {
  icon: React.ReactNode;
  title: string;
  message: string;
};

const getContent = (
  status: string | null,
  error: string | null,
  brand: string,
  content: (key: string) => string | null
): ResultContent => {
  if (status === 'success') {
    return {
      icon: (
        <CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: 40 }} />
      ),
      title:
        content('reactivation.result.success.title') || 'Cevabınız Alındı',
      message:
        content('reactivation.result.success.message') ||
        `Değerli geri bildiriminiz için teşekkür ederiz. ${brand}, siz değerli kullanıcılarımızın katkılarıyla gelişmeye devam ediyor.`,
    };
  }

  if (status === 'error' && error === 'already_answered') {
    return {
      icon: (
        <InfoCircleTwoTone twoToneColor="#1677ff" style={{ fontSize: 40 }} />
      ),
      title:
        content('reactivation.result.already_answered.title') ||
        'Cevabınız Daha Önce Alındı',
      message:
        content('reactivation.result.already_answered.message') ||
        `Bu anket için cevabınızı daha önce aldık. İlginiz için teşekkür ederiz; ${brand}, siz değerli kullanıcılarımızın katkılarıyla gelişmeye devam ediyor.`,
    };
  }

  if (status === 'error' && error === 'not_found') {
    return {
      icon: (
        <CloseCircleTwoTone twoToneColor="#ff4d4f" style={{ fontSize: 40 }} />
      ),
      title:
        content('reactivation.result.not_found.title') || 'Bağlantı Geçersiz',
      message:
        content('reactivation.result.not_found.message') ||
        'Bu bağlantının süresi dolmuş ya da geçersiz görünüyor. Anlayışınız için teşekkür ederiz.',
    };
  }

  if (status === 'error' && error === 'notification_failed') {
    return {
      icon: (
        <CloseCircleTwoTone twoToneColor="#ff4d4f" style={{ fontSize: 40 }} />
      ),
      title:
        content('reactivation.result.notification_failed.title') ||
        'Bir Sorun Oluştu',
      message:
        content('reactivation.result.notification_failed.message') ||
        'Geri dönüşünüzü kaydederken bir sorun yaşadık. Lütfen kısa bir süre sonra tekrar deneyin; sorun devam ederse bizimle iletişime geçebilirsiniz.',
    };
  }

  return {
    icon: (
      <QuestionCircleTwoTone twoToneColor="#8c8c8c" style={{ fontSize: 40 }} />
    ),
    title: content('reactivation.result.unknown.title') || 'Sayfa Bulunamadı',
    message:
      content('reactivation.result.unknown.message') ||
      'Bu sayfaya doğrudan erişim sağlanamıyor.',
  };
};

const ReactivationResult = () => {
  const [params] = useSearchParams();
  const status = params.get('status');
  const error = params.get('error');
  const { provideUrl } = useRouterWithLang();
  const { loginLogo, appTitle } = usePlatform();
  const content = useContent();
  const resultContent = getContent(status, error, appTitle, content);
  // Token artik JS'ten okunamiyor; oturum bilgisi redux'taki me.
  const me = useAppSelector((state) => state.app.setMe.me);
  const isAuthenticated = !!me;

  return (
    <div
      style={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f5f6f8',
        padding: 24,
      }}
    >
      <Card
        style={{
          maxWidth: 440,
          width: '100%',
          borderRadius: 20,
          textAlign: 'center',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
        }}
        bodyStyle={{ padding: '40px 32px' }}
      >
        <Space direction="vertical" size={20} style={{ width: '100%' }}>
          <img
            src={loginLogo}
            alt={appTitle}
            style={{ height: 32, objectFit: 'contain' }}
          />

          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {resultContent.icon}
            <Title level={3} style={{ margin: 0 }}>
              {resultContent.title}
            </Title>
            <Text type="secondary" style={{ fontSize: 15, lineHeight: 1.6 }}>
              {resultContent.message}
            </Text>
          </Space>

          <Button type="primary" size="large" block>
            <Link
              to={
                isAuthenticated
                  ? provideUrl(AppRouteKey.HOME)
                  : provideUrl(AppRouteKey.LOGIN)
              }
            >
              {isAuthenticated
                ? content('reactivation.result.action.home') || 'Ana Sayfa'
                : content('reactivation.result.action.login') || 'Giriş Yap'}
            </Link>
          </Button>
        </Space>
      </Card>
    </div>
  );
};

export default ReactivationResult;
